import {inject, Service} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy} from '@angular/router';
import {PageTitleService} from './page-title.service';

/**
 * A route's `data.pageTitle`.  Either a fixed value, or a function given that route's snapshot --
 * resolved data included -- so pages can title themselves from what they loaded.
 */
export type PageTitle = string | number | PageTitleFn;

export type PageTitleFn = (route: ActivatedRouteSnapshot) => string | number | null | undefined;

/**
 * Sets the browser title on every navigation from the activated route's `data.pageTitle`.  Routes
 * that declare nothing fall back to the bare app name, so only the pages that want a prefix have
 * to say so.
 *
 * We use route data rather than Angular's built in `title` because a `title` ResolveFn runs
 * alongside the route's other resolvers and so cannot see their results.  This runs after
 * activation, when resolved data is available.
 */
@Service()
export class AppTitleStrategy extends TitleStrategy {
  private readonly pageTitleService = inject(PageTitleService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.pageTitleService.setTitle(this.resolvePageTitle(snapshot));
  }

  /**
   * Walks up from the deepest activated route and uses the first `pageTitle` found, so a child
   * can override whatever an ancestor declared.
   */
  private resolvePageTitle(snapshot: RouterStateSnapshot): string | number | null | undefined {
    let route: ActivatedRouteSnapshot | null = snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    for (; route; route = route.parent) {
      const pageTitle = route.data.pageTitle as PageTitle | undefined;

      if (pageTitle === undefined) {
        continue;
      }

      return typeof pageTitle === 'function' ? pageTitle(route) : pageTitle;
    }

    return undefined;
  }
}
